/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useState, useContext, useEffect } from 'react';
import { Metadata } from '@thd-olt-component-react/metadata';
import { QueryProvider, QueryContext, extend } from '@thd-nucleus/data-sources';
import { useLocation } from '@thd-olt-component-react/router';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { ThemeContext, useTheme } from '@thd-olt-component-react/theme-provider';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { SpecialBuyPromos } from '@thd-olt-component-react/special-buy-promos';
import { SpecialBuyProducts } from '@thd-olt-component-react/special-buy-banner';
import { PromotionsEmailSignup } from '@thd-olt-component-react/promotions-email-signup';
import { SpecialBuyMessaging, SeoMessaging, LegalMessaging } from '@thd-olt-component-react/special-buy-messaging';
import { SavingsByDepartment } from '@thd-olt-component-react/savings-by-department';
import { useThdCustomer, CustomerContext } from '@thd-olt-functional/customer-information';
import { CardMedia, Typography } from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import classNames from 'classnames';
import {
  isPro,
  getMetadata,
  getPreviewDateFromParams,
  getSpecialBuyTypeFromPath,
  SBOTD_TYPE,
  PRO_TYPE
} from '../utils/utils';

const dataFailureCallback = (/* { data } */) => {
  throw new Error('invalid special buy metadata. No item-ids in first category.');
};

export const SpecialBuyPage = () => {
  declareContexts([ExperienceContext, QueryContext, ThemeContext, CustomerContext]);
  const { channel } = useContext(ExperienceContext);
  const theme = useTheme(SpecialBuyPage);
  const hidePromotionsEmailSignup = theme?.props?.hidePromotionsEmailSignup || false;
  const [isMounted, setMounted] = useState(false);
  const { isCustomerIdentified = false } = useThdCustomer();
  const isMobile = channel === 'mobile';
  const { pathname, search } = useLocation();
  const previewDate = getPreviewDateFromParams(search);
  const specialBuyType = getSpecialBuyTypeFromPath(pathname); // sbotd, pro
  const metadata = getMetadata(specialBuyType);
  const specialBuyCarouselTypes = [];

  useEffect(() => {
    setMounted(true);
  }, []);

  if (typeof window !== 'undefined') {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('specialBuy.page-load', specialBuyType);
  }

  if (isPro(specialBuyType)) specialBuyCarouselTypes.push(SBOTD_TYPE);
  else specialBuyCarouselTypes.push(PRO_TYPE);

  const cacheKeyString = `special-buy-${isMobile ? 'mobile' : 'desktop'}`;
  const heroWrapperCss = classNames('sui-p-0 sm:sui-mb-4', {
    'sui-inline-flex sui-justify-between sui-w-full': !isMobile
  });

  const heroTextCss = classNames('sui-absolute sui-w-50', {
    'sui-pt-4 sui-pl-3': !isMobile,
    'sui-pt-5 sui-pl-5': isMobile
  });

  const heroCardCss = classNames({
    'sui-p-3': isMobile
  });

  const legalCardCss = classNames({
    'sui-p-3': isMobile,
    'sui-pb-4': !isMobile
  });

  const savingsImage = () => {
    return (
      <img
        src="https://www.homedepot.com/hdus/en_US/DTCCOMNEW/fetch/Global_Assets/sbotd-mktg-pro.jpg"
        alt="saving img"
        height="400"
        width="700"
      />
    );
  };

  const subscriptionImage = () => {
    return (
      <img
        // eslint-disable-next-line max-len
        src="https://www.homedepot.com/hdus/en_US/DTCCOMNEW/fetch/Test_and_Target/UX_Assets/2020-SBF-HomeDelivery-203584334.jpg"
        alt="subscription img"
        height="400"
        width="700"
      />
    );
  };

  return (
    <QueryProvider
      cacheKey={cacheKeyString}
      defaultVariables={{
        isBrandPricingPolicyCompliant: isMounted ? isCustomerIdentified : false
      }}
    >
      {metadata && <Metadata data={metadata} />}
      <article className="sui-max-w-[1440px] sui-mx-auto sui-my-0">
        {!hidePromotionsEmailSignup && (
          <ErrorBoundary name="email-signup">
            <div hidden={isMobile} className="sui-paper sui-p-2 sui-mb-4">
              <PromotionsEmailSignup promotionType={specialBuyType} />
            </div>
          </ErrorBoundary>
        )}
        <ErrorBoundary name="special-buy-messaging">
          <div className="sui-paper sui-mb-1">
            <SpecialBuyMessaging
              specialBuyType={specialBuyType}
              previewDate={previewDate}
            />
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="special-buy-promos">
          <SpecialBuyPromos
            specialBuyType={specialBuyType}
            previewDate={previewDate}
            backupCategories
            dataFailureCallback={dataFailureCallback}
          />
        </ErrorBoundary>
        <ErrorBoundary name="savings-by-department">
          <div className="sui-p-0 sui-mb-4 sm:sui-mt-2 sui-mt-3">
            <SavingsByDepartment />
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="special-buy-carousels">
          {specialBuyCarouselTypes.map((specialBuyCarouselType, index) => (
            <div key={`${specialBuyCarouselType}-carousel`} className="sui-p-0 sui-mb-4">
              <Hydrator
                id={`special-buy-carousels-${index}`}
                scrollBuffer={1000}
              >
                <SpecialBuyProducts promoType={specialBuyCarouselType} />
              </Hydrator>
            </div>
          ))}
        </ErrorBoundary>
        <div className={heroWrapperCss}>
          <a href="https://www.homedepot.com/b/Special-Values/N-5yc1vZ7">
            <div className={heroTextCss}>
              <Typography weight="display" variant={isMobile ? 'h3' : 'h2'} color="inverse">
                EXPLORE ALL SAVINGS
              </Typography>
              <Typography variant="body-sm" color="inverse">
                <div className="sui-pt-3">
                  Shop Now
                  <ArrowForward className="sui-align-middle sui-inline-block sui-fill-brand sui-w-3 sui-h-3" />
                </div>
              </Typography>
            </div>
            <div className={heroCardCss}>
              <CardMedia component={savingsImage} alt="Placeholder image" aspect="wide" />
            </div>
          </a>
          <a href="https://www.homedepot.com/c/subscription_service">
            <div className={heroTextCss}>
              <Typography weight="display" variant={isMobile ? 'h3' : 'h2'} color="inverse">
                HOME DEPOT SUBSCRIPTIONS
              </Typography>
              <Typography variant="body-sm" color="inverse">
                <div className="sui-pt-3">
                  Shop Now
                  <ArrowForward className="sui-align-middle sui-inline-block sui-fill-brand sui-w-3 sui-h-3" />
                </div>
              </Typography>
            </div>
            <div className={heroCardCss}>
              <CardMedia component={subscriptionImage} alt="Placeholder image" aspect="wide" />
            </div>
          </a>
        </div>
        <ErrorBoundary name="legal-messaging">
          <div className={legalCardCss}>
            <LegalMessaging />
          </div>
        </ErrorBoundary>
        <ErrorBoundary name="seo-messaging">
          <div className="sm:sui-p-0 sui-p-3 sui-mb-4">
            <SeoMessaging specialBuyType={specialBuyType} />
          </div>
        </ErrorBoundary>
      </article>
    </QueryProvider>
  );
};

SpecialBuyPage.displayName = 'SpecialBuyPage';

SpecialBuyPage.dataModel = extend(
  PromotionsEmailSignup,
  SpecialBuyPromos,
  SpecialBuyMessaging,
  SpecialBuyProducts,
  SavingsByDepartment
);
